import { Vue, Component, Watch } from "vue-property-decorator";
import { sortBy, union } from "lodash";
import { ReportsModule } from "@/modules/reports-objective";
import { fmtMsg, ActionItem } from "commonui";
import { Checkbox, Select } from "ant-design-vue";
import { ReportsLocale } from "@/locales/localeid";
import Spinning from "@/components/spinning/spinning.vue";
import LabelText from "@/components/label-text/label-text.vue";
import UnitSelect from "@/components/unit-select/unit-select.vue";
import { AccessLevel, MethodologyType, SpecialDesignation } from "@/util/enum";
import { VersionGroupModule } from "@/modules/versiongroup";
import { ILessonPlanVersionModel } from "@/models/lesson-plan";
import { disabledDueToAccessLevel, getRouteAccessLevel } from "@/util/func";

@Component({
	components: {
		Select,
		Spinning,
		Checkbox,
		LabelText,
		ActionItem,
		Option: Select.Option,
		UnitSelect
	}
})
export default class VPCOverviewHeaderActions extends Vue {
	selectedMethodology: number = MethodologyType.Classic;
	versionGroupModule = VersionGroupModule;
	selectedVersionId: any = "";
	reportsModule = ReportsModule;
	curriculumType?: number | null = null;
	isVpcOverview = true;
	accessLevel = AccessLevel;

	created() {
		ReportsModule.setMethodologyType(MethodologyType.Classic);
		ReportsModule.getLessonPlanVersions();
	}

	get methodologyText(): string {
		return fmtMsg(ReportsLocale.ReportsComponentMethodology);
	}

	get versionText(): string {
		return fmtMsg(ReportsLocale.ReportsLessonPlanVersionText);
	}

	get vcsLevelUnits() {
		return sortBy(ReportsModule.vcsUnits, "unit");
	}

	get versionGroups() {
		return this.versionGroupModule.versionGroups;
	}

	get lessonplanVersions() {
		return ReportsModule.lessonplanVersions;
	}

	get exportText() {
		return fmtMsg(ReportsLocale.ReportsExportExcelText);
	}

	get filterMethodologyType(): any {
		return Object.values(MethodologyType).filter((d) => isNaN(d as any));
	}

	get currentRouteAccessLevel() {
		return getRouteAccessLevel(this.$route);
	}

	get disabledDueToAccessLevel() {
		return disabledDueToAccessLevel(this.currentRouteAccessLevel);
	}

	setSelectedUnit(value) {
		ReportsModule.setSelectedUnit(value);
	}

	exportPDF() {
		ReportsModule.getVpcOverviewPDF();
	}

	getLessonPlanVersion() {
		return (
			ReportsModule.lessonplanVersions.find((v) => v.id == this.selectedVersionId) ||
			({} as ILessonPlanVersionModel)
		);
	}

	handleMethodologyChange(value) {
		this.selectedMethodology = value;
		ReportsModule.setMethodologyType(value);
	}

	handleVersionChange(value) {
		this.selectedVersionId = value;
		this.setUnitsByVersionGroup(this.getLessonPlanVersion());
		this.getRelatedVpcCount();
	}

	setUnitsByVersionGroup(lessonPlanVersion) {
		ReportsModule.setLessonplanVersionId(this.selectedVersionId);
		this.curriculumType = this.versionGroups.find(
			(version) => version.id == lessonPlanVersion.versionGroupId
		)?.curriculumType;
	}

	@Watch("lessonplanVersions", { deep: true })
	getDefaultLessonplanVersion(value) {
		const lessonPlanVersion = ReportsModule.lessonplanVersions.length
			? ReportsModule.lessonplanVersions[0]
			: ({} as ILessonPlanVersionModel);
		this.selectedVersionId = lessonPlanVersion?.id;
		this.setUnitsByVersionGroup(lessonPlanVersion);
	}

	@Watch("reportsModule.versionId")
	@Watch("reportsModule.selectedUnit.id", { deep: true })
	getRelatedVpcCount() {
		ReportsModule.setLoading(true);
		if (ReportsModule.versionId && ReportsModule.selectedUnit) {
			ReportsModule.setCurrentVersionGroup();
			Promise.all([
				ReportsModule.getVpcs(),
				ReportsModule.getRelatedVpcCount(),
				ReportsModule.getLessonPlansSimples()
			]).then(([vpcs, relatedCounts, simples]) => {
				ReportsModule.setLessonplanSimples(simples);
				this.formatToTableData(vpcs, relatedCounts, simples);
				vpcs = this.sortVpcs(vpcs);
				ReportsModule.setFormatedVpcs(vpcs);
				ReportsModule.setLoading(false);
			});
		} else {
			ReportsModule.setLessonplanSimples([]);
			ReportsModule.setFormatedVpcs([]);
			ReportsModule.setLoading(false);
		}
	}

	@Watch("versionGroupModule.selectedGsVersion")
	@Watch("versionGroupModule.selectedLsVersion")
	@Watch("selectedMethodology")
	getLessonPlanVersions() {
		ReportsModule.getLessonPlanVersions();
	}

	sortVpcs(vpcs) {
		let vocabulary = vpcs.filter((vpc) => vpc.specialDesignationId === SpecialDesignation.VPC);
		let reviewVocabulary = vpcs.filter((vpc) => vpc.specialDesignationId === SpecialDesignation.ReviewVPC);
		vocabulary = sortBy(vocabulary, "sequence");
		reviewVocabulary = sortBy(reviewVocabulary, [(x) => x.title.toLowerCase()], "title");
		vpcs = union(vocabulary, reviewVocabulary);
		return vpcs;
	}

	formatToTableData(vpcs, relatedCounts, simples) {
		vpcs.map((vpc) => {
			vpc.review = vpc.specialDesignationId === SpecialDesignation.ReviewVPC ? "*" : "";
			const relatedCount = relatedCounts.find((count) => count.vpcId == vpc.id);
			vpc.totalCount = relatedCount?.totalCount || "";
			simples.forEach((simple) => {
				vpc[simple.id] =
					relatedCount?.lessonPlanVPCCounts.find((lsCount) => lsCount.lessonPlanId == simple.id)?.vpcCount ||
					"";
			});
			return vpc;
		});
	}
}
